import React, { useState, useEffect } from 'react';
import { clientsAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import DocumentAttachments from '../components/DocumentAttachments';
import { Plus, Edit2, Trash2, Loader2, Building2, Paperclip } from 'lucide-react';

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    contact_email: '',
    phone: '',
    address: '',
    logo_url: '',
    default_bill_rate: '',
    default_unit_type: 'days'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showDocuments, setShowDocuments] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name,
        contact_name: client.contact_name || '',
        contact_email: client.contact_email || '',
        phone: client.phone || '',
        address: client.address || '',
        logo_url: client.logo_url || '',
        default_bill_rate: client.default_bill_rate || '',
        default_unit_type: client.default_unit_type || 'days'
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '',
        contact_name: '',
        contact_email: '',
        phone: '',
        address: '',
        logo_url: '',
        default_bill_rate: '',
        default_unit_type: 'days'
      });
    }
    setShowModal(true);
    setError('');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
    setShowDocuments(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const data = {
        ...formData,
        default_bill_rate: parseFloat(formData.default_bill_rate) || 0
      };

      if (editingClient) {
        await clientsAPI.update(editingClient.id, data);
      } else {
        await clientsAPI.create(data);
      }
      
      closeModal();
      fetchClients();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;

    try {
      await clientsAPI.delete(id);
      fetchClients();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Clients</h1>
            <p className="mt-1 text-slate-500">Manage client accounts and billing rates</p>
          </div>
          <button
            onClick={() => openModal()}
            className="btn-primary flex items-center gap-2"
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4" />
            Add Client
          </button>
        </div>

        {/* Clients Grid */}
        {clients.length === 0 ? (
          <div className="card-base">
            <div className="card-body text-center py-12">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No clients yet</h3>
              <p className="text-slate-500 mb-4">Get started by adding your first client.</p>
              <button onClick={() => openModal()} className="btn-primary">
                Add Client
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients.map((client) => (
              <div key={client.id} className="card-base">
                <div className="card-body">
                  <div className="flex items-start justify-between mb-4">
                    {client.logo_url ? (
                      <div className="w-32 h-20 rounded-lg overflow-hidden bg-white flex items-center justify-center border border-slate-100 p-1">
                        <img 
                          src={client.logo_url} 
                          alt={`${client.name} logo`} 
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 bg-[#194eb2]/10 rounded-lg flex items-center justify-center">
                        <Building2 className="w-8 h-8 text-[#194eb2]" />
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openModal(client)}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md"
                        data-testid={`edit-client-${client.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{client.name}</h3>
                  
                  {client.contact_name && (
                    <p className="text-sm text-slate-600">{client.contact_name}</p>
                  )}
                  {client.contact_email && (
                    <p className="text-sm text-slate-500">{client.contact_email}</p>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-500 uppercase">Default Bill Rate</p>
                    <p className="text-lg font-semibold text-slate-900 font-mono">
                      ${client.default_bill_rate?.toLocaleString() || '0'}
                      <span className="text-sm font-normal text-slate-500">/{client.default_unit_type === 'hours' ? 'hour' : 'day'}</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              {editingClient ? 'Edit Client' : 'Add Client'}
            </h3>
            
            {/* Tabs for Edit mode */}
            {editingClient && (
              <div className="flex border-b border-slate-200 mb-4">
                <button
                  type="button"
                  onClick={() => setShowDocuments(false)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    !showDocuments 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Details
                </button>
                <button
                  type="button"
                  onClick={() => setShowDocuments(true)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1 ${
                    showDocuments 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Paperclip className="w-4 h-4" />
                  Documents
                </button>
              </div>
            )}
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
            
            {/* Documents Tab */}
            {showDocuments && editingClient ? (
              <div className="py-2">
                <DocumentAttachments
                  entityType="client"
                  entityId={editingClient.id}
                />
              </div>
            ) : (
              /* Details Form */
              <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Client Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input-base"
                  data-testid="client-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="input-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-base"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="input-base"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input-base"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Logo URL
                </label>
                <input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  className="input-base"
                  placeholder="https://example.com/logo.png"
                  data-testid="client-logo-input"
                />
                {formData.logo_url && (
                  <div className="mt-2 p-2 bg-slate-50 rounded-md">
                    <img 
                      src={formData.logo_url} 
                      alt="Logo preview" 
                      className="h-10 object-contain"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Default Bill Rate ($)
                  </label>
                  <input
                    type="number"
                    value={formData.default_bill_rate}
                    onChange={(e) => setFormData({ ...formData, default_bill_rate: e.target.value })}
                    step="0.01"
                    min="0"
                    className="input-base font-mono"
                    data-testid="client-rate-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Unit Type
                  </label>
                  <select
                    value={formData.default_unit_type}
                    onChange={(e) => setFormData({ ...formData, default_unit_type: e.target.value })}
                    className="input-base"
                  >
                    <option value="days">Days</option>
                    <option value="hours">Hours</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-client-btn"
                >
                  {saving ? 'Saving...' : editingClient ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ClientsPage;
